import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react"
import { toast } from "sonner"
import { getDocumentPushStatus } from "@/modules/sage300/infrastructure/sage300DocumentService"
import type { DocPushStatusResponse } from "@/modules/sage300/infrastructure/sage300DocumentService"

export type DocPushStatus = "Queued" | "Processing" | "Completed" | "Failed"

export interface PendingDocPush {
  pushId: string
  label: string
  organizationId: string
  employeeId: string
  status: DocPushStatus
  errorMessage?: string
}

const MAX_POLL_FAILURES = 3
const MAX_POLL_DURATION_MS = 2 * 60 * 1000 // 2 minutes
const POLL_INTERVAL_MS = 4000

interface PollMeta {
  failedAttempts: number
  startedAt: number
  terminated: boolean
}

function mapStatus(result: DocPushStatusResponse): DocPushStatus {
  if (!result.isTerminal) {
    return result.status === "Pending" ? "Queued" : "Processing"
  }
  return result.isSuccess ? "Completed" : "Failed"
}

function isTerminal(status: DocPushStatus): boolean {
  return status === "Completed" || status === "Failed"
}

interface DocPushContextValue {
  pushes: PendingDocPush[]
  activeCount: number
  addPush: (push: PendingDocPush) => void
  updatePush: (
    pushId: string,
    patch: Partial<Omit<PendingDocPush, "pushId" | "label">>,
  ) => void
  clearCompleted: () => void
}

const DocPushContext = createContext<DocPushContextValue | null>(null)

export function DocPushProvider({ children }: { children: ReactNode }) {
  const [pushes, setPushes] = useState<PendingDocPush[]>([])
  const pollMetaRef = useRef<Map<string, PollMeta>>(new Map())

  const markFailed = useCallback((pushId: string, errorMessage: string, label?: string) => {
    const meta = pollMetaRef.current.get(pushId)
    if (meta) pollMetaRef.current.set(pushId, { ...meta, terminated: true })
    setPushes((prev) =>
      prev.map((item) =>
        item.pushId === pushId ? { ...item, status: "Failed", errorMessage } : item,
      ),
    )
    toast.error(`Document push failed${label ? `: ${label}` : ""}`, {
      description: errorMessage,
    })
  }, [])

  const addPush = useCallback((push: PendingDocPush) => {
    pollMetaRef.current.set(push.pushId, {
      failedAttempts: 0,
      startedAt: Date.now(),
      terminated: false,
    })
    setPushes((prev) => [...prev, push])
  }, [])

  const updatePush = useCallback(
    (
      pushId: string,
      patch: Partial<Omit<PendingDocPush, "pushId" | "label">>,
    ) => {
      setPushes((prev) =>
        prev.map((p) => (p.pushId === pushId ? { ...p, ...patch } : p)),
      )
    },
    [],
  )

  const clearCompleted = useCallback(() => {
    setPushes((prev) => prev.filter((p) => !isTerminal(p.status)))
  }, [])

  const activeCount = pushes.filter((p) => !isTerminal(p.status)).length

  // Poll all non-terminal pushes. Effect re-runs whenever `pushes` changes so
  // newly added items are picked up on the next tick. The cleanup return clears
  // the interval on unmount and whenever dependencies change (handles navigate away).
  useEffect(() => {
    const active = pushes.filter((p) => !isTerminal(p.status))
    if (active.length === 0) return

    const intervalId = setInterval(() => {
      active.forEach((p) => {
        const meta = pollMetaRef.current.get(p.pushId)

        // Already terminated by a previous tick — skip until effect re-runs
        if (meta?.terminated) return

        // Stop condition 3: safety timeout
        if (meta && Date.now() - meta.startedAt > MAX_POLL_DURATION_MS) {
          markFailed(p.pushId, "Document push timed out.")
          return
        }

        getDocumentPushStatus(p.organizationId, p.pushId)
          .then((result) => {
            const currentMeta = pollMetaRef.current.get(p.pushId)
            if (currentMeta?.terminated) return

            if (!result) {
              // 404 — counts as a failure
              const attempts = (currentMeta?.failedAttempts ?? 0) + 1
              pollMetaRef.current.set(p.pushId, {
                ...(currentMeta ?? { startedAt: Date.now(), terminated: false }),
                failedAttempts: attempts,
              })
              // Stop condition 2: repeated API errors
              if (attempts >= MAX_POLL_FAILURES) {
                markFailed(p.pushId, "Failed to get push status.")
              }
              return
            }

            // Reset failure count on any successful response
            if (currentMeta) {
              pollMetaRef.current.set(p.pushId, { ...currentMeta, failedAttempts: 0 })
            }

            // Stop condition 1: terminal state
            const newStatus = mapStatus(result)
            if (result.isTerminal) {
              if (currentMeta) {
                pollMetaRef.current.set(p.pushId, { ...currentMeta, terminated: true })
              }
              if (result.isSuccess) {
                toast.success(`Document pushed to Sage 300: ${p.label}`)
              } else {
                const errMsg = result.lastErrorPayload ?? "Push failed"
                toast.error(`Document push failed: ${p.label}`, { description: errMsg })
              }
            }
            setPushes((prev) =>
              prev.map((item) => {
                if (item.pushId !== p.pushId) return item
                return {
                  ...item,
                  status: newStatus,
                  errorMessage:
                    !result.isSuccess && result.isTerminal
                      ? (result.lastErrorPayload ?? "Push failed")
                      : item.errorMessage,
                }
              }),
            )
          })
          .catch(() => {
            const currentMeta = pollMetaRef.current.get(p.pushId)
            if (currentMeta?.terminated) return

            const attempts = (currentMeta?.failedAttempts ?? 0) + 1
            pollMetaRef.current.set(p.pushId, {
              ...(currentMeta ?? { startedAt: Date.now(), terminated: false }),
              failedAttempts: attempts,
            })
            // Stop condition 2: network failure
            if (attempts >= MAX_POLL_FAILURES) {
              markFailed(p.pushId, "Lost connection to server.")
            }
          })
      })
    }, POLL_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [pushes, markFailed])

  return (
    <DocPushContext.Provider
      value={{ pushes, activeCount, addPush, updatePush, clearCompleted }}
    >
      {children}
    </DocPushContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDocPushContext(): DocPushContextValue {
  const ctx = useContext(DocPushContext)
  if (!ctx)
    throw new Error("useDocPushContext must be used inside <DocPushProvider>")
  return ctx
}
